<template>
  <div class="scan-page">
    <div class="scan-header">
      <img src="../assets/logo_igreja.png" alt="El Shaday" class="scan-logo" />
      <h1 class="scan-title">Check-in</h1>
      <p class="scan-subtitle">Escaneie o QR Code do participante</p>
    </div>

    <!-- Modal Bagagem/Alergia -->
    <div class="modal-overlay" v-if="pendingCheckin" @click.self="cancelCheckin">
      <div class="modal-card checkin-modal">
        <h2 class="modal-title">{{ pendingCheckin.name || 'Check-in' }}</h2>

        <div class="form-group">
          <label class="label">Bagagens *</label>
          <input class="input" type="number" min="0" v-model.number="checkinForm.baggage" placeholder="Numero de volumes" />
        </div>

        <div class="form-group">
          <label class="label">Alergia / Medicacao</label>
          <input class="input" type="text" v-model="checkinForm.allergy" placeholder="Ex: amendoim, insulina..." />
        </div>

        <div class="form-group">
          <label class="label">Fotos da mala (opcional)</label>
          <div class="baggage-photos">
            <div class="photo-slot" v-for="(photo, idx) in checkinForm.photos" :key="idx">
              <img :src="photo.preview" alt="Mala" class="photo-preview" />
              <button type="button" class="photo-remove" @click="removePhoto(idx)" aria-label="Remover foto">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"><path fill="currentColor" d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12z"/></svg>
              </button>
            </div>
            <label class="photo-slot photo-add" v-if="checkinForm.photos.length < 2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M20 5h-3.17L15 3H9L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-8 13c-2.76 0-5-2.24-5-5s2.24-5 5-5s5 2.24 5 5s-2.24 5-5 5z"/></svg>
              <span>Adicionar</span>
              <input type="file" accept="image/*" capture="environment" class="file-input" @change="onPhotoSelected" />
            </label>
          </div>
          <p class="baggage-hint" v-if="checkinForm.photos.length === 0">Tire ate 2 fotos da mala do encontrista</p>
        </div>

        <div class="modal-actions">
          <button class="btn-secondary" @click="cancelCheckin">Cancelar</button>
          <button class="btn-primary btn-confirm" @click="confirmCheckin" :disabled="checkinForm.baggage === null || checkinForm.baggage === '' || confirming">
            <span v-if="!confirming">Confirmar Check-in</span>
            <span v-else>Confirmando...</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Resultado -->
    <div class="result-card card result-success" v-if="result && !result.error">
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"><path fill="currentColor" d="m10.6 16.6l7.05-7.05l-1.4-1.4l-5.65 5.65l-2.85-2.85l-1.4 1.4l4.25 4.25ZM12 22q-2.075 0-3.9-.788t-3.175-2.137q-1.35-1.35-2.137-3.175T2 12q0-2.075.788-3.9t2.137-3.175q1.35-1.35 3.175-2.137T12 2q2.075 0 3.9.788t3.175 2.137q1.35 1.35 2.138 3.175T22 12q0 2.075-.788 3.9t-2.137 3.175q-1.35 1.35-3.175 2.138T12 22Z"/></svg>
      <span class="result-name">{{ result.participant.name }}</span>
      <span class="result-msg" v-if="result.alreadyCheckedIn">Ja estava confirmado</span>
      <span class="result-msg" v-else>Confirmado com sucesso!</span>
      <div class="result-photos" v-if="result.participant.baggagePhotos && result.participant.baggagePhotos.length">
        <img v-for="(p, i) in result.participant.baggagePhotos" :key="i" :src="p.url" alt="Mala" class="result-photo" />
      </div>
      <span class="result-warning" v-if="result.photoUploadWarning">{{ result.photoUploadWarning }}</span>
      <button class="btn-primary scan-again-btn" @click="resetScan">Escanear outro</button>
    </div>

    <div class="result-card card result-error" v-else-if="result && result.error">
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24"><path fill="currentColor" d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10s10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17L12 13.41L8.41 17L7 15.59L10.59 12L7 8.41L8.41 7L12 10.59L15.59 7L17 8.41L13.41 12L17 15.59z"/></svg>
      <span class="result-name">Erro</span>
      <span class="result-msg">{{ result.message }}</span>
      <button class="btn-primary scan-again-btn" @click="resetScan">Tentar novamente</button>
    </div>

    <!-- Loading -->
    <div class="scan-loading" v-else-if="processing">
      <div class="spinner"></div>
      <span>Processando...</span>
    </div>

    <!-- Modo: Camera ativa -->
    <div class="scanner-wrapper" v-else-if="mode === 'camera'">
      <div class="scanner-controls">
        <button class="camera-toggle" @click="switchCamera">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><path fill="currentColor" d="M20 5h-3.17L15 3H9L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-8 13c-2.76 0-5-2.24-5-5s2.24-5 5-5s5 2.24 5 5s-2.24 5-5 5z"/></svg>
          <span>{{ usingFront ? 'Traseira' : 'Frontal' }}</span>
        </button>
        <button class="camera-toggle" @click="mode = 'choose'">
          <span>Voltar</span>
        </button>
      </div>
      <div class="scanner-container">
        <div id="qr-reader" class="qr-reader"></div>
      </div>
    </div>

    <!-- Modo: Escolha (tela principal) -->
    <div class="options-card card" v-else-if="mode === 'choose'">
      <!-- Opcao 1: Camera (so mostra se suporta) -->
      <button class="option-btn" @click="tryCamera" v-if="canUseCamera">
        <div class="option-icon option-camera">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M20 5h-3.17L15 3H9L7.17 5H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm-8 13c-2.76 0-5-2.24-5-5s2.24-5 5-5s5 2.24 5 5s-2.24 5-5 5z"/></svg>
        </div>
        <span class="option-label">Abrir Camera</span>
        <span class="option-desc">Escanear QR em tempo real</span>
      </button>

      <!-- Opcao 2: Tirar foto / galeria -->
      <label class="option-btn">
        <div class="option-icon option-photo">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M19 7v2.99s-1.99.01-2 0V7h-3s.01-1.99 0-2h3V2h2v3h3v2h-3zm-3 4V8h-3V5H5c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-8h-3zM5 19l3-4l2 3l3-4l4 5H5z"/></svg>
        </div>
        <span class="option-label">Tirar Foto do QR</span>
        <span class="option-desc">Usa a camera nativa do celular</span>
        <input type="file" accept="image/*,.heic,.heif" capture="environment" class="file-input" @change="onFileSelected" />
      </label>

      <!-- Opcao 3: Escolher da galeria -->
      <label class="option-btn">
        <div class="option-icon option-gallery">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24"><path fill="currentColor" d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z"/></svg>
        </div>
        <span class="option-label">Escolher da Galeria</span>
        <span class="option-desc">Selecionar imagem salva</span>
        <input type="file" accept="image/*,.heic,.heif" class="file-input" @change="onFileSelected" />
      </label>

      <p class="camera-note" v-if="!canUseCamera">
        Camera em tempo real requer conexao HTTPS. Use as opcoes acima.
      </p>
    </div>

    <!-- Hidden container for file scan -->
    <div id="qr-reader-file-temp" style="display:none;"></div>
  </div>
</template>

<script>
import { Html5Qrcode } from 'html5-qrcode'
import axios from 'axios'
import { getToken } from '../lib/auth'

export default {
  name: 'ScanPage',
  data() {
    return {
      mode: 'choose',
      scanner: null,
      scanning: false,
      processing: false,
      result: null,
      cameras: [],
      currentCameraIndex: 0,
      usingFront: false,
      apiUrl: '',
      pendingCheckin: null,
      checkinForm: { baggage: 0, allergy: '', photos: [] },
      confirming: false
    }
  },
  computed: {
    canUseCamera() {
      return window.isSecureContext
    }
  },
  created() {
    const configured = process.env.VUE_APP_API_URL || ''
    if (configured.includes('localhost') && window.location.hostname !== 'localhost') {
      this.apiUrl = configured.replace('localhost', window.location.hostname)
    } else {
      this.apiUrl = configured
    }
  },
  beforeUnmount() {
    this.stopScanner()
  },
  methods: {
    async tryCamera() {
      try {
        this.cameras = await Html5Qrcode.getCameras()
        if (this.cameras.length === 0) {
          this.result = { error: true, message: 'Nenhuma camera encontrada. Use a opcao de tirar foto.' }
          return
        }
        this.mode = 'camera'
        await this.$nextTick()
        this.startScanner()
      } catch {
        this.result = { error: true, message: 'Permissao de camera negada. Use a opcao de tirar foto.' }
      }
    },

    async startScanner() {
      try {
        this.scanner = new Html5Qrcode('qr-reader')
        let cameraConfig
        if (this.cameras.length > 0) {
          cameraConfig = { deviceId: { exact: this.cameras[this.currentCameraIndex].id } }
        } else {
          cameraConfig = { facingMode: this.usingFront ? 'user' : 'environment' }
        }
        await this.scanner.start(
          cameraConfig,
          { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1 },
          (text) => this.onScanSuccess(text),
          () => {}
        )
        this.scanning = true
      } catch {
        this.mode = 'choose'
        this.result = { error: true, message: 'Erro ao iniciar camera. Use a opcao de tirar foto.' }
      }
    },

    async stopScanner() {
      if (this.scanner && this.scanning) {
        try { await this.scanner.stop() } catch { /* already stopped */ }
        this.scanning = false
      }
    },

    async switchCamera() {
      await this.stopScanner()
      if (this.cameras.length > 1) {
        this.currentCameraIndex = (this.currentCameraIndex + 1) % this.cameras.length
      }
      this.usingFront = !this.usingFront
      await this.$nextTick()
      this.startScanner()
    },

    normalizeImage(file) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          const maxSize = 1024
          let width = img.width
          let height = img.height
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
          }
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], 'qr.png', { type: 'image/png' }))
            } else {
              reject(new Error('Canvas conversion failed'))
            }
          }, 'image/png')
        }
        img.onerror = () => reject(new Error('Image load failed'))
        img.src = URL.createObjectURL(file)
      })
    },

    async onFileSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      this.processing = true
      try {
        const normalized = await this.normalizeImage(file)
        const tempScanner = new Html5Qrcode('qr-reader-file-temp')
        const result = await tempScanner.scanFile(normalized, true)
        await this.doCheckin(result)
        tempScanner.clear()
      } catch {
        this.result = { error: true, message: 'Nao foi possivel ler o QR Code. Tente com a foto mais nitida e centralizada.' }
      } finally {
        this.processing = false
        event.target.value = ''
      }
    },

    async onScanSuccess(scannedId) {
      if (this.processing) return
      this.processing = true
      await this.stopScanner()
      await this.doCheckin(scannedId)
      this.processing = false
    },

    async doCheckin(participantId) {
      try {
        const headers = {}
        const token = getToken()
        if (token) headers.Authorization = `Bearer ${token}`
        // Buscar dados do participante primeiro
        const checkResponse = await axios.post(`${this.apiUrl}/scan/${participantId}`, { dryRun: true }, { headers })
        if (checkResponse.data.alreadyCheckedIn) {
          this.result = checkResponse.data
          return
        }
        // Abrir modal para preencher bagagem/alergia
        const participant = checkResponse.data.participant
        this.pendingCheckin = { id: participantId, name: participant?.name || 'Participante' }
        this.checkinForm = {
          baggage: participant?.baggage ?? 0,
          allergy: participant?.allergy || '',
          photos: []
        }
      } catch (err) {
        const message = err.response?.data?.message || 'QR Code invalido ou servidor indisponivel'
        this.result = { error: true, message }
      }
    },

    async onPhotoSelected(event) {
      const file = event.target.files[0]
      event.target.value = ''
      if (!file || this.checkinForm.photos.length >= 2) return
      try {
        const normalized = await this.normalizeBaggagePhoto(file)
        const preview = URL.createObjectURL(normalized)
        this.checkinForm.photos.push({ file: normalized, preview })
      } catch {
        // Fallback: usar arquivo original se canvas falhar
        const preview = URL.createObjectURL(file)
        this.checkinForm.photos.push({ file, preview })
      }
    },

    removePhoto(idx) {
      const [removed] = this.checkinForm.photos.splice(idx, 1)
      if (removed?.preview) URL.revokeObjectURL(removed.preview)
    },

    normalizeBaggagePhoto(file) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          const maxSize = 1600
          let width = img.width
          let height = img.height
          if (width > maxSize || height > maxSize) {
            const ratio = Math.min(maxSize / width, maxSize / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
          }
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0, width, height)
          canvas.toBlob((blob) => {
            if (blob) {
              resolve(new File([blob], `mala-${Date.now()}.jpg`, { type: 'image/jpeg' }))
            } else {
              reject(new Error('Canvas conversion failed'))
            }
          }, 'image/jpeg', 0.85)
        }
        img.onerror = () => reject(new Error('Image load failed'))
        img.src = URL.createObjectURL(file)
      })
    },

    cancelCheckin() {
      this.checkinForm.photos.forEach(p => p.preview && URL.revokeObjectURL(p.preview))
      this.checkinForm.photos = []
      this.pendingCheckin = null
      this.resetScan()
    },

    async confirmCheckin() {
      this.confirming = true
      try {
        const headers = {}
        const token = getToken()
        if (token) headers.Authorization = `Bearer ${token}`
        const formData = new FormData()
        formData.append('baggage', String(this.checkinForm.baggage ?? 0))
        formData.append('allergy', this.checkinForm.allergy || '')
        this.checkinForm.photos.forEach(p => formData.append('baggagePhotos', p.file))
        const response = await axios.post(`${this.apiUrl}/scan/${this.pendingCheckin.id}`, formData, { headers })
        this.checkinForm.photos.forEach(p => p.preview && URL.revokeObjectURL(p.preview))
        this.checkinForm.photos = []
        this.pendingCheckin = null
        this.result = response.data
      } catch (err) {
        const message = err.response?.data?.message || 'Erro ao confirmar check-in'
        this.result = { error: true, message }
        this.pendingCheckin = null
      } finally {
        this.confirming = false
      }
    },

    async resetScan() {
      this.result = null
      this.processing = false
      if (this.mode === 'camera') {
        await this.$nextTick()
        this.startScanner()
      }
    }
  }
}
</script>

<style scoped>
.scan-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: var(--spacing-base);
  background: var(--color-bg-body);
}
.scan-header {
  text-align: center;
  margin-bottom: var(--spacing-lg);
}
.scan-logo {
  width: 60px;
  height: 60px;
  border-radius: var(--radius-circle);
  object-fit: cover;
  margin-bottom: var(--spacing-md);
}
.scan-title {
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--color-text-primary);
  margin-bottom: var(--spacing-sm);
}
.scan-subtitle {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

/* Options */
.options-card {
  width: 100%;
  max-width: 400px;
  padding: var(--spacing-base);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}
.option-btn {
  display: flex;
  align-items: center;
  gap: var(--spacing-base);
  padding: var(--spacing-base);
  border: 2px solid var(--color-bg-separator);
  border-radius: var(--radius-lg);
  background: var(--color-bg-surface);
  cursor: pointer;
  transition: var(--transition-fast);
  text-align: left;
  text-decoration: none;
  position: relative;
}
.option-btn:hover {
  border-color: var(--color-accent);
  box-shadow: var(--shadow-button);
}
.option-icon {
  width: 56px;
  height: 56px;
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.option-camera { background: rgba(51,102,204,0.1); color: var(--color-accent); }
.option-photo { background: rgba(46,204,113,0.1); color: var(--color-success); }
.option-gallery { background: rgba(155,89,182,0.1); color: #9b59b6; }
.option-label {
  font-size: var(--text-md);
  font-weight: 600;
  color: var(--color-text-primary);
  display: block;
}
.option-desc {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  display: block;
  margin-top: 2px;
}
.file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.camera-note {
  text-align: center;
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  padding-top: var(--spacing-sm);
  border-top: 1px solid var(--color-bg-separator);
}

/* Scanner */
.scanner-wrapper {
  width: 100%;
  max-width: 400px;
}
.scanner-controls {
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--spacing-md);
}
.camera-toggle {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: 6px 14px;
  background: var(--color-bg-surface);
  border: 1px solid #ddd;
  border-radius: var(--radius-md);
  font-size: var(--text-xs);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: var(--transition-fast);
}
.camera-toggle:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
}
.scanner-container {
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-card);
}
.qr-reader { width: 100%; }

/* Resultado */
.result-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl);
  width: 100%;
  max-width: 400px;
  text-align: center;
}
.result-success { color: var(--color-success); }
.result-success .result-name { font-size: var(--text-xl); font-weight: 700; color: var(--color-text-primary); }
.result-success .result-msg { font-size: var(--text-md); color: var(--color-success); font-weight: 600; }
.result-error { color: var(--color-danger); }
.result-error .result-name { font-size: var(--text-xl); font-weight: 700; color: var(--color-danger); }
.result-error .result-msg { font-size: var(--text-base); color: var(--color-text-secondary); }
.scan-again-btn { margin-top: var(--spacing-md); }

/* Loading */
.scan-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl);
  color: var(--color-text-muted);
}
.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-bg-separator);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* Modal checkin */
.checkin-modal { max-width: 400px; }
.form-group { margin-bottom: var(--spacing-base); }
.btn-confirm {
  background: var(--color-success);
  border-color: var(--color-success);
}
.btn-confirm:hover { background: #27ae60; border-color: #27ae60; }
.btn-confirm:disabled { opacity: 0.5; cursor: not-allowed; }

/* Fotos da mala */
.baggage-photos {
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
  margin-top: var(--spacing-xs);
}
.photo-slot {
  position: relative;
  width: 96px;
  height: 96px;
  border-radius: var(--radius-md);
  overflow: hidden;
  background: var(--color-bg-separator);
  flex-shrink: 0;
}
.photo-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.photo-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0,0,0,0.65);
  color: #fff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.photo-remove:hover { background: rgba(0,0,0,0.85); }
.photo-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 2px dashed #bbb;
  background: transparent;
  color: var(--color-text-muted);
  cursor: pointer;
  font-size: var(--text-xs);
  transition: var(--transition-fast);
}
.photo-add:hover {
  border-color: var(--color-accent);
  color: var(--color-accent);
}
.baggage-hint {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  margin-top: var(--spacing-xs);
}

/* Fotos no resultado */
.result-photos {
  display: flex;
  gap: var(--spacing-sm);
  margin-top: var(--spacing-sm);
}
.result-photo {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-bg-separator);
}
.result-warning {
  font-size: var(--text-xs);
  color: #e67e22;
  background: rgba(230,126,34,0.1);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-sm);
  margin-top: var(--spacing-sm);
}
</style>
